const ALLOWED_TAGS = new Set([
  'b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li',
  'span', 'code', 'pre', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'blockquote', 'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'div', 'hr', 'sub', 'sup', 'small', 'mark', 'del', 'ins',
]);
const ALLOWED_ATTRS = new Set(['href', 'target', 'rel', 'class', 'src', 'alt', 'width', 'height']);
const DANGEROUS_URI_RE = /^\s*(javascript|data|vbscript)\s*:/i;

/**
 * Sanitize HTML to prevent XSS attacks.
 * Strips dangerous tags/attributes while keeping safe formatting.
 * Lightweight alternative to DOMPurify for simple use cases.
 */
export function sanitizeHtml(html: string): string {
  if (!html || typeof html !== 'string') return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  sanitizeNode(doc.body);
  return doc.body.innerHTML;
}

function sanitizeNode(node: Node): void {
  const toRemove: Node[] = [];

  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const el = child as Element;
      const tag = el.tagName.toLowerCase();

      // Remove script, style, iframe, object, embed, form, input, etc.
      if (!ALLOWED_TAGS.has(tag)) {
        // Keep text content of removed elements (except script/style)
        if (tag === 'script' || tag === 'style' || tag === 'iframe' || tag === 'object' || tag === 'embed') {
          toRemove.push(child);
        } else {
          // Replace unknown tag with its children
          const fragment = document.createDocumentFragment();
          while (el.firstChild) {
            fragment.appendChild(el.firstChild);
          }
          sanitizeNode(fragment);
          node.replaceChild(fragment, child);
        }
        return;
      }

      // Remove all non-whitelisted attributes
      Array.from(el.attributes).forEach((attr) => {
        const attrName = attr.name.toLowerCase();
        // Block ALL event handlers (on*)
        if (attrName.startsWith('on') || !ALLOWED_ATTRS.has(attrName)) {
          el.removeAttribute(attr.name);
        }
      });

      // Sanitize href attributes (case-insensitive, handles URL-encoding)
      if (el.hasAttribute('href')) {
        const href = decodeURIComponent(el.getAttribute('href') || '');
        if (DANGEROUS_URI_RE.test(href)) {
          el.removeAttribute('href');
        }
      }

      // Sanitize src attributes
      if (el.hasAttribute('src')) {
        const src = decodeURIComponent(el.getAttribute('src') || '');
        if (DANGEROUS_URI_RE.test(src)) {
          el.removeAttribute('src');
        }
      }

      sanitizeNode(child);
    } else if (child.nodeType === Node.COMMENT_NODE) {
      toRemove.push(child);
    }
  });

  toRemove.forEach((n) => {
    try { node.removeChild(n); } catch { /* already removed */ }
  });
}

/**
 * Sanitize and render HTML as React dangerouslySetInnerHTML prop value.
 */
export function createSafeHtml(html: string): { __html: string } {
  return { __html: sanitizeHtml(html) };
}
